import { useRef, useState, type DragEvent } from "react";
import { Link } from "@tanstack/react-router";
import { CircleNotchIcon, CheckCircleIcon, WarningCircleIcon, PlugsIcon, CircleIcon, ChatCircleDotsIcon } from "@phosphor-icons/react";
import { LoadingLines } from "./Loading";
import { useWorkspacePrefetch } from "./workspace-queries";
import type { SavedRun } from "../../../packages/contracts/src/workspace";
import type { WorkflowSummary } from "./api";

export function runStatus(status: string) {
  switch (status) {
    case "queued": return { label: "Queued", tone: "neutral", icon: CircleIcon };
    case "running": return { label: "Running", tone: "running", icon: CircleNotchIcon };
    case "succeeded": return { label: "Completed", tone: "complete", icon: CheckCircleIcon };
    case "unconfirmed": return { label: "Completed, not confirmed", tone: "attention", icon: WarningCircleIcon };
    // waiting is a production run's status; waiting_for_you is a run record's status.
    case "waiting": case "waiting_for_you": return { label: "Waiting for you", tone: "attention", icon: ChatCircleDotsIcon };
    case "failed": return { label: "Failed", tone: "failed", icon: WarningCircleIcon };
    case "disconnected": return { label: "Connection lost", tone: "disconnected", icon: PlugsIcon };
    case "cancelled": return { label: "Cancelled", tone: "neutral", icon: CircleIcon };
    default: return { label: status.replaceAll("_", " "), tone: "neutral", icon: CircleIcon };
  }
}
export function RunStatus({ status, iconOnly = false }: { status: string; iconOnly?: boolean }) {
  const { label, tone, icon: Icon } = runStatus(status);
  return <span className={`run-status run-status-${tone}`} title={label}>
    <Icon size={14} className={status === "running" ? "spin" : undefined} aria-hidden="true" />
    {!iconOnly && <span>{label}</span>}
  </span>;
}
export function runName(run: SavedRun) { return run.run_label || runDate(run.created_at); }
export function runDate(value: string) {
  return new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const failed = (status?: string) => status === "failed";
/** The sidebar mark: red when the last run failed, blue while it runs. */
function markOf(method: WorkflowSummary) {
  const o = method.overview;
  if (!o) return null;
  if (failed(o.last_run?.status)) return { tone: "attention", label: "The last run failed" };
  if (o.last_run?.status === "running") return { tone: "running", label: "Running" };
  return null;
}
const USED_LIMIT = 12;

/**
 * The Method lists in the sidebar: Pinned (kept by the person, saved on the server, in their order) and Used this week
 * (kept by Method: the newest last run first, at most 12). Pins reorder by drag or Alt+Arrow.
 */
export function MethodNavigation({ workflows, selectedMethod, onPins, loading, error }: {
  workflows: WorkflowSummary[];
  selectedMethod?: string;
  onPins: (ids: string[]) => void;
  loading?: boolean;
  error?: string;
}) {
  const prefetch = useWorkspacePrefetch();
  const suppressClick = useRef(false);
  const [dragged, setDragged] = useState<string | null>(null);
  const [drop, setDrop] = useState<{ id: string; after: boolean } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const active = workflows.filter(method => !method.archived_at);
  const pinned = active.filter(method => method.pinned_position != null).sort((a, b) => a.pinned_position! - b.pinned_position!);
  const used = active.filter(method => method.pinned_position == null && (method.overview?.runs_7d ?? 0) > 0)
    .sort((a, b) => (b.overview?.last_run?.created_at ?? "").localeCompare(a.overview?.last_run?.created_at ?? "") || a.id.localeCompare(b.id))
    .slice(0, USED_LIMIT);
  function move(id: string, target: string, after: boolean) {
    if (id === target) return;
    const order = pinned.map(method => method.id).filter(item => item !== id);
    if (!order.includes(target)) return;
    order.splice(order.indexOf(target) + Number(after), 0, id);
    onPins(order);
    setAnnouncement(`${pinned.find(method => method.id === id)?.name} moved ${after ? "after" : "before"} ${pinned.find(method => method.id === target)?.name}.`);
  }
  function methodRow(method: WorkflowSummary, canMove: boolean) {
    const current = selectedMethod === method.id;
    const mark = markOf(method);
    const below = (event: DragEvent) => { const bounds = event.currentTarget.getBoundingClientRect(); return event.clientY > bounds.top + bounds.height / 2; };
    const accepts = canMove && !!dragged && dragged !== method.id;
    return <div className="sidebar-method" key={method.id}>
      <div className={`sidebar-method-row${drop?.id === method.id ? drop.after ? " drop-after" : " drop-before" : ""}`}
        onDragOver={event => {
          if (!accepts) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setDrop({ id: method.id, after: below(event) });
        }}
        onDragLeave={() => setDrop(current => current?.id === method.id ? null : current)}
        onDrop={event => {
          event.preventDefault();
          if (accepts) move(dragged!, method.id, below(event));
          setDragged(null); setDrop(null);
        }}>
        <Link to="/methods/$workflowId" params={{ workflowId: method.id }} draggable={canMove}
          onMouseEnter={() => prefetch.method(method.id)} onFocus={() => prefetch.method(method.id)}
          className={current ? "current" : ""} aria-current={current ? "page" : undefined}
          title={mark ? `${method.name} · ${mark.label}` : method.name}
          aria-keyshortcuts={canMove ? "Alt+ArrowUp Alt+ArrowDown" : undefined}
          onPointerDown={() => { suppressClick.current = false; }}
          onClick={event => { if (suppressClick.current) event.preventDefault(); }}
          onDragStart={canMove ? event => {
            suppressClick.current = true;
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", method.id);
            setDragged(method.id);
          } : undefined}
          onDragEnd={() => { setDragged(null); setDrop(null); }}
          onKeyDown={canMove ? event => {
            suppressClick.current = false;
            if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
            event.preventDefault();
            const after = event.key === "ArrowDown";
            const target = pinned[pinned.findIndex(item => item.id === method.id) + (after ? 1 : -1)];
            if (target) move(method.id, target.id, after);
          } : undefined}>
          <span>{method.name}</span>
          {mark && <i className={`sb-mark is-${mark.tone}`} aria-label={mark.label} role="img" />}
        </Link>
      </div>
    </div>;
  }
  if (loading) return <div className="sidebar-recents sidebar-methods sidebar-methods-loading"><span className="sidebar-label">Used this week</span><LoadingLines rows={6} label="Loading methods" /></div>;
  return <div className="sidebar-recents sidebar-methods">
    {pinned.length > 0 && <><span className="sidebar-label">Pinned</span>{pinned.map(method => methodRow(method, true))}</>}
    <span className="sidebar-label">Used this week</span>
    {used.map(method => methodRow(method, false))}
    {!used.length && <p>{error ? "Could not load methods" : "No Method ran this week."}</p>}
    {active.length > 0 && <Link className="sidebar-all-methods" to="/methods">All {active.length} Methods</Link>}
    <span className="visually-hidden" role="status">{announcement}</span>
  </div>;
}
